import React from 'react'

import { VariableSizeList } from 'react-window'

type WindowedMenuListProps = {
  children: React.ReactNode
  maxHeight?: number
}

type ChildElement = React.ReactElement & {
  props: {
    children?: React.ReactNode
    data?: {
      options?: any[]
      data?: { name?: string }
      isDefault?: boolean
      [key: string]: any
    }
    isFocused?: boolean
    type?: string
    [key: string]: any
  }
}

//* locale helpers
function flattenGroupedChildren(children: ChildElement[]): ChildElement[] {
  return children.reduce<ChildElement[]>((result, child) => {
    const {
      props: { children: nestedChildren = [], data: { options: nestedOptions = [] } = {} },
      props: childProps,
    } = child

    const isGrouped = nestedOptions.length > 0

    if (!isGrouped) {
      // when create new option
      return [...result, React.cloneElement(child, childProps)]
    }

    return [...result, React.cloneElement(child, { type: 'group' }, []), ...(nestedChildren as ChildElement[])]
  }, [])
}

function getCurrentIndex(children: ChildElement[]): number {
  return Math.max(
    children.findIndex(({ props: { isFocused } = {} }) => !!isFocused),
    0
  )
}

//* custom UI integration component
export function WindowedMenuList({ children: childrenProp, maxHeight = 0 }: WindowedMenuListProps) {
  const listRef = React.useRef<VariableSizeList>(null)

  const children = flattenGroupedChildren(React.Children.toArray(childrenProp) as ChildElement[])

  const currentIndex = getCurrentIndex(children)

  React.useEffect(() => {
    const ref = listRef && listRef.current
    if (ref) {
      if (children.length === 1) {
        ref.resetAfterIndex(0)
      }
      // enables scrolling on key down arrow
      if (currentIndex >= 0) {
        ref.scrollToItem(currentIndex)
      }
    }
  })

  const [heights, totalHeight] = React.useMemo(() => {
    const childrenHeights = children.map(({ props: { type, data } = {} }) => {
      if (type === 'group') {
        return 36
      }
      if (data?.data?.name || data?.isDefault) {
        // kontír, payment form - paidthrough select
        return 48
      }
      return 32
    })
    return [childrenHeights, childrenHeights.reduce((acc, curr) => (acc += curr), 0)]
  }, [children])

  return (
    <VariableSizeList
      ref={listRef}
      height={Math.min(maxHeight, totalHeight)}
      width="100%"
      itemCount={children.length}
      itemSize={index => heights[index]}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </VariableSizeList>
  )
}
